import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { cn } from '../../../lib/utils';

interface EquipmentNotesProps {
    html: string;
}

const isEmpty = (html: string) =>
    !new DOMParser()
        .parseFromString(html, 'text/html')
        .body.textContent?.trim();

export default function EquipmentNotes({ html }: EquipmentNotesProps) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const el = bodyRef.current;
        if (el && !expanded) setOverflows(el.scrollHeight > el.clientHeight);
    }, [html, expanded]);

    if (isEmpty(html)) {
        return (
            <span className="text-xs font-medium text-subtle-foreground dark:text-neutral-300">
                {t('roomOverview.equipment.noNotes')}
            </span>
        );
    }

    return (
        <div
            className={cn(
                'flex gap-2',
                expanded ? 'flex-col items-start' : 'items-baseline'
            )}
        >
            <div
                ref={bodyRef}
                className={cn(
                    'text-xs leading-relaxed text-subtle-foreground dark:text-neutral-300 [&_li]:font-normal [&_ol]:ml-4 [&_ol]:list-decimal [&_p]:font-normal [&_ul]:ml-4 [&_ul]:list-disc',
                    expanded
                        ? '[&_p]:mb-1 [&_p:last-child]:mb-0'
                        : 'line-clamp-1 min-w-0 flex-1'
                )}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
            />
            {(overflows || expanded) && (
                <button
                    type="button"
                    onClick={() => setExpanded((open) => !open)}
                    className="shrink-0 text-xs font-medium text-subtle-foreground hover:text-foreground hover:underline"
                >
                    {t(
                        expanded
                            ? 'roomOverview.equipment.viewLess'
                            : 'roomOverview.equipment.viewMore'
                    )}
                </button>
            )}
        </div>
    );
}
